import React, { useState } from 'react';
import axios from 'axios';
import './style/UploadMultipleImg.css';
import { useEventContext } from '../contexts/EventContext';
import { SUBSCRIPTION_LIMITS } from '../constants/subscriptionRules';
import { useTranslation } from 'react-i18next';

const baseUrl = process.env.REACT_APP_API_URL;
const endpointuploadmultipleimg = `${baseUrl}/upload-img`;


// ⚠️ Vérifie que `REACT_APP_API_URL` est bien défini

function isEventPastSeuil(seuilFr) {
  if (!seuilFr) return false;
  const [datePart] = seuilFr.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const seuilDate = new Date(year, month - 1, day);
  seuilDate.setHours(0, 0, 0, 0);
  seuilDate.setDate(seuilDate.getDate() + 2);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now > seuilDate;
}

function isLimitReached(data) {
  if (!data?.Sub) return false;
  const limits = SUBSCRIPTION_LIMITS[data.Sub];
  if (!limits) return false;
  const current = data.numberpicture ?? data.NumberPicture ?? 0;
  return current >= limits.maxPictures;
}

function UploadMultipleImg({ eventId }) {
  const [files, setFiles] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const { events, refetchEvents, loadingEvents } = useEventContext();
  const event = events.find(e => `${e.id || e.eventId}` === `${eventId}`);

  let eventBlocked = false;
  let eventMsg = '';
  if (event) {
    if (isEventPastSeuil(event.Seuildepassement)) {
      eventBlocked = true;
      eventMsg = "⚠️ Vous avez atteint la date limite de l'événement. L'envoi d'images n'est plus possible.";
    } else if (isLimitReached(event)) {
      eventBlocked = true;
      eventMsg = "⚠️ Limite d’images atteinte pour cet événement. Impossible d’ajouter d'autres photos.";
    }
  }

  const handleExpand = () => {
    setSuccess('');
    setError('');
    setFiles([]);
    setUploading(false);
    setExpanded(prev => !prev);
  };

  const handleFileChange = (e) => {
    const selected = [...e.target.files];
    setFiles(selected);
    console.log('[📦] Fichiers sélectionnés :', selected.map(f => f.name));
  };

  const compressImage = (file, maxSize = 3000, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (ev) => {
        img.src = ev.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            const readerCompressed = new FileReader();
            readerCompressed.onloadend = () => {
              const base64Data = readerCompressed.result.split(',')[1];
              resolve(base64Data);
            };
            readerCompressed.readAsDataURL(blob);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = reject;
    });
  };

  const handleSubmit = async () => {
    setSuccess('');
    setError('');

    if (!files.length) {
      setError("Merci de sélectionner au moins une image.");
      return;
    }

    if (event) {
      if (isEventPastSeuil(event.Seuildepassement)) {
        setError("⚠️ Vous avez atteint la date limite de l'événement.");
        return;
      }
      if (isLimitReached(event)) {
        setError("⚠️ Limite d’images atteinte.");
        return;
      }
    }

    setUploading(true);
    let successCount = 0;
    let failureCount = 0;

    for (const file of files) {
      try {
        const base64Data = await compressImage(file);
        const payload = {
          items: [{
            name: file.name,
            data: base64Data,
            eventId: eventId,
            sub: event?.Sub || 'P0',
          }]
        };

        const response = await axios.post(endpointuploadmultipleimg, payload, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data.statusCode >= 400) {
          throw new Error(response.data.message || 'Erreur inconnue');
        }

        successCount++;
      } catch (err) {
        console.error(`[❌] ${file.name} :`, err.message);
        failureCount++;
      }
    }

    if (successCount > 0) setSuccess(`${successCount} image(s) envoyée(s) avec succès.`);
    if (failureCount > 0) setError(`${failureCount} échec(s) lors de l'envoi.`);

    setFiles([]);
    setUploading(false);

    if (typeof refetchEvents === 'function') {
      await refetchEvents();
    }
  };

  return (
    <div className="upload-multiple-container">
      <button type="button" className="upload-toggle-button" onClick={handleExpand}>
        {t('MultipleImg.titlebutton')}
        <span className={`arrow ${expanded ? 'open' : ''}`}>➤</span>
      </button>

      {expanded && (
        <div className="upload-fields">
          {loadingEvents && <div>{t('MultipleImg.Loading')}</div>}
          {eventBlocked && <p className="error-message">{eventMsg}</p>}
          {!eventBlocked && (
            <>
              <div className="custom-file-upload">
                <label htmlFor="multiUpload">📷 {t('MultipleImg.ChooseImages')}</label>
                <input
                  id="multiUpload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {files.length > 0 && (
                  <p className="file-name">
                    {files.length === 1 ? files[0].name : `${files.length} fichiers sélectionnés`}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={uploading}
              >
                {uploading ? t('MultipleImg.EnvoieImages') : t('MultipleImg.SendImages')}
              </button>

            </>
          )}
        </div>
      )}

      {success && <p className="success-message">{success}</p>}
      {error && <p className="error-message">{error}</p>}
    </div>
  );
}

export default UploadMultipleImg;
